import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TextInput from '@wix/da-ds/pkg/formControls/dsPresets/TextInput';
import MessageModal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import CancelButton from '@wix/da-ds/pkg/modals/dsPresets/MessageModal/buttons/CancelButton';
import ConfirmButton from '@wix/da-ds/pkg/modals/dsPresets/MessageModal/buttons/ConfirmButton';
import DestructiveConfirmButton from '@wix/da-ds/pkg/modals/dsPresets/MessageModal/buttons/DestructiveConfirmButton';
import { VisuallyHidden } from '@wix/da-a11y/pkg/components';
import { type Editor } from '@tiptap/core';
import { getMarkRange, getMarkType, isiOS } from '@tiptap/react';
import { useKeyDown } from '@wix/da-hooks/pkg/useKeyDown';
import { isTouch } from '../../../../utils/browser';
import {
  addMissingProtocol,
  validateHref,
} from '../../../../utils/validateHref';
import EditorAnchor from '@wix/da-ds/pkg/Icons/24x24/EditorAnchor';
import IconButtonWithContextMenu from '@wix/da-ds/pkg/Dropdown/Preset/IconButtonWithContextMenu';
import cn from 'classnames';
import { popperDropdownConfig } from '../../../../../extensions/extension-deviation/constants';

import s from './EditLinkMenuModal.scss';

export interface Props {
  editor: Editor;
  onCancel: () => void;
  onSave: ({ text, link }: { text: string; link: string }) => void;
  onRemove: () => void;
}

const Form = React.forwardRef<any, any>(
  ({ initialText, existingHref, onSave, isNew, editor }, formRef) => {
    const { t } = useTranslation('editor');
    const [textValue, setTextValue] = useState(initialText);
    const [hrefValue, setHrefValue] = useState(existingHref);
    // I think it's safe to assume that `existingHref` has been validated since we validate
    // before allowing the modal to submit. Therefore, anything preexisting has been validated.
    const [isHrefValid, setIsHrefValid] = useState(!!existingHref);
    const [shouldShowHrefErrorMessage, setShouldShowHrefErrorMessage] =
      useState(false);
    const [anchorsOnPage, setAnchorsOnPage] = useState<string[]>([]);

    const doesUrlFieldHaveError = hrefValue.length > 0 && !isHrefValid;
    const handleSubmit: React.FormEventHandler<HTMLFormElement> = useCallback(
      event => {
        if (!doesUrlFieldHaveError && textValue !== '' && hrefValue !== '') {
          onSave({
            text: textValue,
            link: addMissingProtocol(hrefValue),
            initialText,
          });
        } else {
          setShouldShowHrefErrorMessage(true);
        }
        event.preventDefault();
        return false;
      },
      [doesUrlFieldHaveError, onSave, textValue, hrefValue, initialText]
    );

    useEffect(() => {
      const ids: string[] = [];
      const windowAnchors = Array.from(
        document.querySelectorAll('div[data-editor-viewer] a[id]')
      );

      (editor as Editor).state.doc.descendants(node => {
        if (node.type.name === 'anchor') {
          ids.push(node.attrs.id);
        }
      });

      if (windowAnchors.length > 0) {
        const windowIds = windowAnchors
          .map(a => a.getAttribute('id'))
          .filter(i => i !== null && !/site-header/.test(i));

        ids.push(...(windowIds as any));
      }

      if (ids.length) {
        setAnchorsOnPage([...new Set(ids)]);
      }
    }, [editor]);

    const handleSetAnchor = useCallback(e => {
      setHrefValue(`#${e.target.textContent}`);
      setIsHrefValid(true);
    }, []);

    return (
      <form ref={formRef} className={s['root']} onSubmit={handleSubmit}>
        <TextInput
          id="text"
          label={t('ext.linkMenu.modal.form.input.text.label')}
          type="text"
          value={textValue}
          isRequired
          onChange={event => setTextValue(event.target.value)}
        />
        <div
          className={cn(s['link'], anchorsOnPage.length > 0 && s['anchors'])}
        >
          <TextInput
            id="text"
            label={t('ext.linkMenu.modal.form.input.href.label')}
            placeholder={t('ext.linkMenu.modal.form.input.href.placeholder')}
            type="text"
            value={hrefValue}
            isRequired
            validationResult={doesUrlFieldHaveError ? 'error' : undefined}
            details={
              doesUrlFieldHaveError && shouldShowHrefErrorMessage
                ? t('ext.linkMenu.modal.form.input.href.errorMessage')
                : undefined
            }
            onFocus={() => setShouldShowHrefErrorMessage(false)}
            onBlur={() => setShouldShowHrefErrorMessage(true)}
            onChange={event => {
              setHrefValue(event.target.value.trim());
              setIsHrefValid(
                validateHref(addMissingProtocol(event.target.value.trim()))
              );
            }}
          />
          {anchorsOnPage.length > 0 && (
            <IconButtonWithContextMenu
              onClick={e => {
                setShouldShowHrefErrorMessage(false);
                e.preventDefault();
              }}
              className={s['anchor']}
              icon={EditorAnchor}
              size="medium"
              items={anchorsOnPage.map(anchor => ({
                label: anchor,
                onClick: handleSetAnchor,
              }))}
              tooltipHoverDelay={500}
              aria-label={t('ext.linkMenu.modal.form.anchormenu.tooltip')}
              tooltipText={t('ext.linkMenu.modal.form.anchormenu.tooltip')}
              popperManagerProps={popperDropdownConfig}
            />
          )}
        </div>
        <VisuallyHidden>
          {/*
      This is here to make sure that native form UX works as expected (e.g. submit attempt on "enter" key presses)

      Ideally, we'd convert the modal body to a `<form>` element, but that's out of the scope of my current task.
    */}
          <button type="submit" tabIndex={-1}>
            {t('ext.linkMenu.modal.form.a11y.submitButton.label')}
          </button>
        </VisuallyHidden>
      </form>
    );
  }
);

const EditLinkMenuModal: FC<Props> = ({
  editor,
  onCancel,
  onSave,
  onRemove,
}) => {
  const { t } = useTranslation('editor');
  let existingHref = editor.isActive('link')
    ? (editor.getAttributes('link').href as string)
    : '';

  // If the attrib is not set we probably on IOS so get it from the handler storage
  if (
    existingHref === '' &&
    isiOS() &&
    !editor?.storage?.linkBubbleMenuHandler?.bubbleMenuOptions?.isNew
  ) {
    existingHref =
      editor.storage.linkBubbleMenuHandler?.linkNodeAttrs?.href ?? '';
  }

  const linkRange = getMarkRange(
    editor.state.selection.$from,
    getMarkType('link', editor.schema)
  );
  const linkText = linkRange
    ? editor.state.doc.textBetween(linkRange.from, linkRange.to)
    : '';

  const selectedText = editor.state.doc.textBetween(
    editor.state.selection.$from.pos,
    editor.state.selection.$to.pos
  );

  const initialText = linkText || selectedText;

  const formRef = useRef<HTMLFormElement>(null);
  const handleConfirmClick = useCallback(() => {
    if (formRef.current) {
      formRef.current?.requestSubmit();
    }
  }, []);

  // If the user presses escape, we should cancel
  useKeyDown({ Escape: onCancel });

  const isNewLink = !existingHref;

  return (
    <MessageModal
      useFixedMobileHeight
      isOpen
      onRequestClose={onCancel}
      heading={
        isNewLink
          ? t('ext.linkMenu.modal.title.add')
          : t('ext.linkMenu.modal.title.edit')
      }
      footerRight={[
        <CancelButton
          key="cancel"
          onClick={onCancel}
          className={cn(isTouch() && s['small-cancel'])}
        >
          {t('ext.linkMenu.modal.buttons.cancel')}
        </CancelButton>,
        isTouch() && !isNewLink && (
          <DestructiveConfirmButton onClick={onRemove}>
            {t('ext.linkMenu.modal.buttons.remove')}
          </DestructiveConfirmButton>
        ),
        <ConfirmButton
          key="confirm"
          onClick={handleConfirmClick}
          // Hidden because the form has its own submit button
          aria-hidden="true"
        >
          {t('ext.linkMenu.modal.buttons.confirm')}
        </ConfirmButton>,
      ]}
      showFooterBorder
      showHeaderBorder
    >
      <Form
        editor={editor}
        ref={formRef}
        key={`link-at-${linkRange ? `${linkRange.from}-${linkRange.to}` : ''}`}
        initialText={initialText}
        onSave={onSave}
        existingHref={existingHref}
        isNew={isNewLink}
      />
    </MessageModal>
  );
};
EditLinkMenuModal.displayName = 'EditLinkMenuModal';

export default EditLinkMenuModal;
